import { create } from 'zustand';
import type { AnalysisDepth, CrawlMode, Viewport } from '@/types';

/**
 * Local UI preferences that seed the New Analysis form. These are convenience
 * defaults only; the server still validates and clamps every submitted value.
 */

export interface AnalysisDefaults {
  crawlMode: CrawlMode;
  maxPages: number;
  viewports: Viewport[];
  depth: AnalysisDepth;
  visualAnalysis: boolean;
  cssAnalysis: boolean;
  componentAnalysis: boolean;
  responsiveAnalysis: boolean;
}

const STORAGE_KEY = 'design-md-preferences';

export const DEFAULT_ANALYSIS_DEFAULTS: AnalysisDefaults = {
  crawlMode: 'single',
  maxPages: 5,
  viewports: ['desktop', 'mobile'],
  depth: 'standard',
  visualAnalysis: true,
  cssAnalysis: true,
  componentAnalysis: true,
  responsiveAnalysis: true,
};

function read(): AnalysisDefaults {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_ANALYSIS_DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<AnalysisDefaults>;
    return { ...DEFAULT_ANALYSIS_DEFAULTS, ...parsed };
  } catch {
    return DEFAULT_ANALYSIS_DEFAULTS;
  }
}

interface PreferencesState {
  defaults: AnalysisDefaults;
  setDefaults: (patch: Partial<AnalysisDefaults>) => void;
  reset: () => void;
}

export const usePreferencesStore = create<PreferencesState>((set, get) => ({
  defaults: read(),
  setDefaults: (patch) => {
    const next = { ...get().defaults, ...patch };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Persisting is best-effort.
    }
    set({ defaults: next });
  },
  reset: () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore.
    }
    set({ defaults: DEFAULT_ANALYSIS_DEFAULTS });
  },
}));
